import { SubscriptionTypes } from "constants/subscriptionTypes";

export const SUBSCRIPTION_CONFIG_RULES = {
  [SubscriptionTypes.ONE_TIME]: {
    showDiscountFields: true,
    showPricingStrategy: true,
    showGlobalPricing: true,
    showCustomPricing: true,
    showCoolingOffPeriod: true,
    showRenewalSettings: true,
    showCancellationPolicy: true,
    showBillingConfiguration: true,
    showProRataRefund: false,
    showTrialPeriod: false,
    showBillingCycles: false,
    autoSetStrategy: null,
    pricingStrategyOptions: ["global", "custom"],
    defaultPricingStrategy: "global",
    allowDiscountInput: true,
    maxDiscountPercentage: 100,
  },

  [SubscriptionTypes.SUBSCRIPTION_NO_CANCELLATION]: {
    showDiscountFields: true,
    showPricingStrategy: true,
    showGlobalPricing: true,
    showCustomPricing: true,
    showCoolingOffPeriod: true,
    showRenewalSettings: true,
    showCancellationPolicy: true,
    showBillingConfiguration: true,
    showProRataRefund: false,
    showTrialPeriod: true,
    showBillingCycles: true,
    autoSetStrategy: null,
    pricingStrategyOptions: ["global", "custom"],
    defaultPricingStrategy: "global",
    allowDiscountInput: true,
    maxDiscountPercentage: 100,
  },

  // [SubscriptionTypes.SUBSCRIPTION_CANCELLATION_ALLOWED]: {
  //   showDiscountFields: false,
  //   showPricingStrategy: true,
  //   showGlobalPricing: false,
  //   showCustomPricing: false,
  //   showCoolingOffPeriod: true,
  //   showRenewalSettings: true,
  //   showCancellationPolicy: true,
  //   showBillingConfiguration: true,
  //   showProRataRefund: true,
  //   showTrialPeriod: true,
  //   showBillingCycles: true,
  //   autoSetStrategy: null,
  //   pricingStrategyOptions: ['global', 'custom'],
  //   defaultPricingStrategy: 'global',
  //   allowDiscountInput: false,
  //   maxDiscountPercentage: 0
  // }
  [SubscriptionTypes.SUBSCRIPTION_CANCELLATION_ALLOWED]: {
    showDiscountFields: false,
    showPricingStrategy: true,
    showGlobalPricing: false,
    showCustomPricing: true, // Now true to allow custom item selection
    showCoolingOffPeriod: true,
    showRenewalSettings: true,
    showCancellationPolicy: true,
    showBillingConfiguration: true,
    showProRataRefund: true,
    showTrialPeriod: true,
    showBillingCycles: true,
    autoSetStrategy: null,
    pricingStrategyOptions: ["global", "custom"],
    defaultPricingStrategy: "global",
    allowDiscountInput: false,
    maxDiscountPercentage: 0,
  },
};

export const getSubscriptionConfigRules = (subscriptionType) => {
  return (
    SUBSCRIPTION_CONFIG_RULES[subscriptionType] ||
    SUBSCRIPTION_CONFIG_RULES[SubscriptionTypes.ONE_TIME]
  );
};

export const shouldShowSection = (subscriptionType, sectionName) => {
  const rules = getSubscriptionConfigRules(subscriptionType);
  return rules[sectionName] === true;
};

export const isDiscountInputAllowed = (subscriptionType) => {
  const rules = getSubscriptionConfigRules(subscriptionType);
  return rules.allowDiscountInput || false;
};

export const getMaxDiscountPercentage = (subscriptionType) => {
  const rules = getSubscriptionConfigRules(subscriptionType);
  return rules.maxDiscountPercentage || 100;
};

export const getDiscountPolicyMessage = (subscriptionType) => {
  switch (subscriptionType) {
    case SubscriptionTypes.SUBSCRIPTION_CANCELLATION_ALLOWED:
      return {
        title: "Flexible Subscription Pricing",
        message:
          "Flexible subscriptions provide cancellation flexibility. Standard pricing applies with no discounts available.",
        type: "info",
        icon: "ℹ️",
        showPricingStrategy: true,
        allowDiscount: false,
      };
    case SubscriptionTypes.ONE_TIME:
      return {
        title: "Discounts Available",
        message:
          "One-time purchases can have discounts applied for upfront payment.",
        type: "success",
        icon: "✅",
        showPricingStrategy: true,
        allowDiscount: true,
      };
    case SubscriptionTypes.SUBSCRIPTION_NO_CANCELLATION:
      return {
        title: "Discounts Available",
        message:
          "Locked-in subscriptions can have discounts for customer commitment.",
        type: "success",
        icon: "✅",
        showPricingStrategy: true,
        allowDiscount: true,
      };
    default:
      return {
        title: "Pricing Configuration",
        message: "Configure pricing for this subscription type.",
        type: "info",
        icon: "ℹ️",
        showPricingStrategy: true,
        allowDiscount: true,
      };
  }
};

export const getPricingStrategyOptions = (subscriptionType) => {
  const rules = getSubscriptionConfigRules(subscriptionType);
  return rules.pricingStrategyOptions || [];
};

export const getAutoSetStrategy = (subscriptionType) => {
  const rules = getSubscriptionConfigRules(subscriptionType);
  return rules.autoSetStrategy;
};

// Get entity label based on module type
export const getEntityLabel = (moduleType, entityConfig) => {
  // Entities are grouped by context (e.g. ENTITY_CONFIG.SUBSCRIPTION.PRODUCT)
  const config =
    entityConfig[moduleType] || entityConfig.SUBSCRIPTION?.[moduleType];

  if (!moduleType || !config) {
    return "items";
  }

  return (config.plural || config.label).toLowerCase();
};

export const getContextDisplayNames = (
  durationId,
  countryId,
  subscriptionType,
  durations = [],
  countries = []
) => {
  const duration = durations.find((d) => d.id === durationId);
  const country = countries.find((c) => c.id === countryId);

  // console.log("duration : ", duration);

  return {
    // durationDisplay: duration
    //   ? duration.name
    //   : durationId?.replace("_", " ") || "selected duration",
    durationDisplay: duration
      ? duration.value + " " + duration.unit
      : durationId?.replace("_", " ") || "selected duration",
    countryDisplay: country
      ? country.countryName
      : countryId || "selected country",
    subscriptionTypeDisplay:
      subscriptionType?.replace(/_/g, " ").toLowerCase() || "subscription",
  };
};

export const getContextMessages = {
  pricingStrategy: (moduleType, entityConfig) =>
    `How do you want to price your ${getEntityLabel(
      moduleType,
      entityConfig
    )}?`,

  coolingOffPeriod: (durationDisplay, countryDisplay, entityLabel) =>
    `Set cooling-off period for ${durationDisplay} subscription in ${countryDisplay} for ${entityLabel}`,

  renewalSettings: (durationDisplay, entityLabel) =>
    `Configure renewal settings for ${durationDisplay} subscription of ${entityLabel}`,

  cancellationPolicy: (durationDisplay, entityLabel) =>
    `Set cancellation policy for ${durationDisplay} subscription of ${entityLabel}`,

  billingSettings: (subscriptionTypeDisplay, entityLabel) =>
    `Configure billing for ${subscriptionTypeDisplay} of ${entityLabel}`,
};
